export type DevicePlatform = "android" | "ios" | "other";
export type PwaDisplayMode = "twa" | "standalone" | "browser";

export function getDevicePlatform(): DevicePlatform {
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  // iPadOS 13+ Safari reports a Mac UA. `navigator.platform` is deprecated, so
  // we infer iPad by combining the Mac UA with multi-touch support — a desktop
  // Mac reports maxTouchPoints === 0.
  if (ua.includes("Macintosh") && navigator.maxTouchPoints > 1) return "ios";
  return "other";
}

export function getPwaDisplayMode(): PwaDisplayMode {
  if (document.referrer.startsWith("android-app://")) return "twa";
  const standalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone ? "standalone" : "browser";
}

// iOS Chrome/Firefox/Edge cannot "Add to Home Screen" as a functional PWA;
// only Safari supports it. Used to hide the install banner on iOS non-Safari.
export function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  if (getDevicePlatform() !== "ios") return false;
  return !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
}

// In-app webviews can't install PWAs and can't open native browser install
// prompts. Hide the banner rather than show a CTA that goes nowhere.
// UA tokens:
//   FBAN/FBAV          — Facebook
//   Instagram          — Instagram
//   Twitter            — X / Twitter
//   TelegramBot/Telegram — Telegram (TelegramBot first so it doesn't get swallowed)
//   LinkedInApp        — LinkedIn
//   Line/              — LINE
//   MicroMessenger     — WeChat
//   KAKAOTALK          — KakaoTalk
//   musical_ly|Bytedance — TikTok (musical_ly is the legacy token still emitted)
//   Snapchat           — Snapchat
//   Discord            — Discord
//   Slack              — Slack
//   Pinterest          — Pinterest
//   ; wv\)             — generic Android System WebView (e.g. embedded by random apps)
export function isInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /FBAN|FBAV|Instagram|Twitter|TelegramBot|Telegram|LinkedInApp|Line\/|MicroMessenger|KAKAOTALK|musical_ly|Bytedance|Snapchat|Discord|Slack|Pinterest|; wv\)/i.test(
    navigator.userAgent,
  );
}
